// Authentication helper utilities

// Timeout configuration constants
export const TIMEOUT_CONFIG = {
  AUTO_LOGOUT_HOURS: {
    ADMIN: 1.0, // Admin: 1 hour of inactivity
    USER: 0.1667, // User: 10 minutes of inactivity
    RESELLER: 0.1667, // Reseller: 10 minutes of inactivity
    DEFAULT: 0.1667 // Default: 10 minutes of inactivity
  },
  TOKEN_VALIDATION_MINUTES: 5, // Check token every 5 minutes
  API_TIMEOUT_MS: 30000, // API request timeout
  SESSION_WARNING_MINUTES: 5 // Show warning 5 minutes before expiry
};

export const AUTH_STORAGE_KEYS = {
  TOKEN: 'token',
  USER_ID: 'userId',
  ROLE_NAME: 'roleName',
  MOBILE_NUMBER: 'mobile_number',
  EMAIL: 'email',
  MENU_PERMISSIONS: 'menuPermissions',
  SESSION_START_TIME: 'sessionStartTime',
  LAST_ACTIVITY_TIME: 'lastActivityTime'
};

// Function to decode JWT token and return detailed information
export const decodeJWTToken = (token) => {
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const expirationTime = payload.exp;
    const issuedAt = payload.iat;
    
    return {
      payload,
      isExpired: expirationTime < currentTime,
      expiresAt: new Date(expirationTime * 1000),
      issuedAt: new Date(issuedAt * 1000),
      timeUntilExpiry: expirationTime - currentTime,
      timeUntilExpiryMinutes: Math.floor((expirationTime - currentTime) / 60),
      timeUntilExpiryHours: Math.floor((expirationTime - currentTime) / 3600)
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Function to decode JWT token and check expiration
export const isTokenExpired = (token) => {
  const tokenInfo = decodeJWTToken(token);
  return tokenInfo ? tokenInfo.isExpired : true;
};

// Function to get current token information
export const getCurrentTokenInfo = () => {
  const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  return decodeJWTToken(token);
};

// Function to log token information for debugging
export const logTokenInfo = () => {
  const tokenInfo = getCurrentTokenInfo();
  if (tokenInfo) {
    //console.log('=== JWT Token Information ===');
    //console.log('Issued at:', tokenInfo.issuedAt.toLocaleString());
    //console.log('Expires at:', tokenInfo.expiresAt.toLocaleString());
    //console.log('Time until expiry:', tokenInfo.timeUntilExpiryMinutes, 'minutes');
    //console.log('Is expired:', tokenInfo.isExpired);
    //console.log('Token payload:', tokenInfo.payload);
    //console.log('============================');
  } else {
    //console.log('No valid token found in session storage');
  }
};

export const isAuthenticated = () => {
  const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  const roleName = sessionStorage.getItem(AUTH_STORAGE_KEYS.ROLE_NAME);
  
  // Check if token exists and is not expired
  if (!token || !roleName || isTokenExpired(token)) {
    // Clear expired/invalid auth data
    clearAuthData();
    return false;
  }
  
  return true;
};

export const getUserRole = () => {
  return sessionStorage.getItem(AUTH_STORAGE_KEYS.ROLE_NAME);
};

export const getAuthToken = () => {
  return sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
};

export const clearAuthData = () => {
  Object.values(AUTH_STORAGE_KEYS).forEach(key => {
    sessionStorage.removeItem(key);
  });
};

export const setAuthData = (userData) => {
  if (!userData) return;
  
  sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, userData.token);
  sessionStorage.setItem(AUTH_STORAGE_KEYS.USER_ID, userData.user_id);
  sessionStorage.setItem(AUTH_STORAGE_KEYS.ROLE_NAME, userData.user_type);
  sessionStorage.setItem(AUTH_STORAGE_KEYS.MOBILE_NUMBER, userData.mobile_number);
  sessionStorage.setItem(AUTH_STORAGE_KEYS.EMAIL, userData.email);
  
  if (userData.menu_permissions) {
    sessionStorage.setItem(AUTH_STORAGE_KEYS.MENU_PERMISSIONS, JSON.stringify(userData.menu_permissions));
  }
  
  // Initialize session timing
  initializeSession();
};

export const getDefaultRoute = (userType) => {
  switch (userType) {
    case 'ADMIN':
      return '/AdminDashboard';
    case 'RESELLER':
      return '/dashboard';
    default:
      return '/dashboard';
  }
};

export const shouldUsePageReload = (userType) => {
  // Use page reload for ADMIN users to ensure proper route generation
  return userType === 'ADMIN';
};

// Function to set up periodic token validation
export const setupTokenValidation = () => {
  // Check token every configured minutes
  const validationInterval = setInterval(() => {
    if (!isAuthenticated()) {
      // Token is expired or invalid, redirect to login
      clearInterval(validationInterval);
      window.location.href = "/home";
    }
  }, TIMEOUT_CONFIG.TOKEN_VALIDATION_MINUTES * 60 * 1000); // Configured minutes

  // Return the interval ID so it can be cleared if needed
  return validationInterval;
};

// Function to clear token validation interval
export const clearTokenValidation = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};

// Session management functions for activity-based expiry
export const getSessionStartTime = () => {
  const startTime = sessionStorage.getItem(AUTH_STORAGE_KEYS.SESSION_START_TIME);
  return startTime ? new Date(startTime) : null;
};

export const getLastActivityTime = () => {
  const lastActivity = sessionStorage.getItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY_TIME);
  return lastActivity ? new Date(lastActivity) : null;
};

export const updateLastActivityTime = () => {
  const now = new Date().toISOString();
  sessionStorage.setItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY_TIME, now);
};

export const initializeSession = () => {
  const now = new Date().toISOString();
  sessionStorage.setItem(AUTH_STORAGE_KEYS.SESSION_START_TIME, now);
  sessionStorage.setItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY_TIME, now);
  
  // Log role and timeout configuration
  const userRole = getUserRole();
  const timeoutHours = TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS[userRole] || TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS.DEFAULT;
  const timeoutMinutes = Math.floor(timeoutHours * 60);
  //console.log(`🔐 Session initialized for role: "${userRole}" with ${timeoutMinutes} minute inactivity timeout`);
  
  if (!TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS[userRole]) {
    //console.warn(`⚠️ Role "${userRole}" not found in timeout config. Using DEFAULT: ${Math.floor(TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS.DEFAULT * 60)} minutes`);
  }
};

export const extendSession = () => {
  // Reset the session start time to current time, effectively extending based on user role
  const now = new Date().toISOString();
  sessionStorage.setItem(AUTH_STORAGE_KEYS.SESSION_START_TIME, now);
  sessionStorage.setItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY_TIME, now);
  
  const userRole = getUserRole() || 'DEFAULT';
  const timeoutForRole = TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS[userRole];
  
  if (!timeoutForRole) {
    console.error(`❌ Timeout not found for role: "${userRole}". Available roles:`, Object.keys(TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS));
  }
  
  const timeoutHours = timeoutForRole || TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS.DEFAULT;
  const timeoutMinutes = Math.floor(timeoutHours * 60);
  //console.log(`✅ Session extended: ${timeoutMinutes} minute inactivity timeout reset for "${userRole}"`);
  return true;
};

export const getSessionTimeRemaining = () => {
  const lastActivity = getLastActivityTime();
  if (!lastActivity) return 0;
  
  const now = new Date();
  const userRole = getUserRole() || 'DEFAULT';
  
  // Only log warnings, not every call (runs every 5 seconds)
  const timeoutForRole = TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS[userRole];
  if (!timeoutForRole) {
    console.warn(`⚠️ Timeout not found for role: "${userRole}" (type: ${typeof userRole}). Available roles:`, Object.keys(TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS));
  }
  
  const timeoutHours = timeoutForRole || TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS.DEFAULT;
  const sessionDurationMs = timeoutHours * 60 * 60 * 1000;
  const elapsedMs = now - lastActivity; // Use lastActivity for idle detection
  const remainingMs = sessionDurationMs - elapsedMs;
  
  return Math.max(0, Math.floor(remainingMs / 1000)); // Return seconds
};

export const shouldShowSessionWarning = () => {
  const timeRemaining = getSessionTimeRemaining();
  const warningThreshold = TIMEOUT_CONFIG.SESSION_WARNING_MINUTES * 60; // Convert to seconds
  
  return timeRemaining > 0 && timeRemaining <= warningThreshold;
};

export const isSessionExpired = () => {
  return getSessionTimeRemaining() <= 0;
};

// Debug helper to log current session status
export const logSessionStatus = () => {
  const userRole = getUserRole();
  const timeRemaining = getSessionTimeRemaining();
  const lastActivity = getLastActivityTime();
  const sessionStart = getSessionStartTime();
  const configuredTimeout = TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS[userRole] || TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS.DEFAULT;
  const now = new Date();
  const elapsedSinceActivity = lastActivity ? Math.floor((now - lastActivity) / 1000) : 0;
  
  // ('═══════════════════════════════════════');
  // console.log('📊 SESSION STATUS DEBUG');
  // console.log('═══════════════════════════════════════');
  // console.log(`🔑 User Role: "${userRole}" (type: ${typeof userRole})`);
  // console.log(`⏱️  Configured Timeout: ${configuredTimeout} hours (${Math.floor(configuredTimeout * 60)} minutes)`);
  // console.log(`🕐 Session Start: ${sessionStart?.toLocaleString() || 'Not set'}`);
  // console.log(`🖱️  Last Activity: ${lastActivity?.toLocaleString() || 'Not set'}`);
  // console.log(`⏰ Time Since Last Activity: ${Math.floor(elapsedSinceActivity / 60)}m ${elapsedSinceActivity % 60}s`);
  // console.log(`⏳ Time Remaining: ${Math.floor(timeRemaining / 60)}m ${timeRemaining % 60}s`);
  // console.log(`✅ Is Expired: ${isSessionExpired()}`);
  // console.log(`⚠️  Should Show Warning: ${shouldShowSessionWarning()}`);
  // console.log('───────────────────────────────────────');
  // console.log('Avconsole.logailable roles in config:', Object.keys(TIMEOUT_CONFIG.AUTO_LOGOUT_HOURS));
  // console.log('═══════════════════════════════════════');
}; 